/**
 * components/confirm-sheet.tsx
 *
 * Cute bottom-sheet confirmation for destructive actions.
 * Used for delete confirmations on milestones and countdowns.
 */
import { useEffect, useRef } from 'react'

interface ConfirmSheetProps {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  emoji?: string
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export default function ConfirmSheet({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Keep it',
  emoji = '💔',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmSheetProps) {
  const cancelledRef = useRef(false)

  useEffect(() => {
    if (open) {
      cancelledRef.current = false
    }
  }, [open])

  if (!open) return null

  const handleConfirm = () => {
    if (cancelledRef.current) return
    cancelledRef.current = true
    onConfirm()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[rgba(44,24,16,0.25)] z-[60] backdrop-blur-[2px] animate-fade-in"
        onClick={onCancel}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        role="button"
        tabIndex={-1}
        aria-label="Close"
      />

      {/* Sheet */}
      <div
        className="
        fixed bottom-0 left-0 right-0 z-[70]
        bg-white rounded-t-[24px]
        shadow-[0_-4px_40px_rgba(0,0,0,0.12)]
        animate-slide-up
        pb-[env(safe-area-inset-bottom)]
        max-w-[430px] mx-auto
      "
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-[36px] h-[4px] rounded-full bg-[#F4C0D1]" />
        </div>

        {/* Content */}
        <div className="px-5 pt-4 pb-6 flex flex-col items-center text-center">
          <div className="text-[48px] mb-4 animate-bounce-in">{emoji}</div>
          <h2 className="font-['Playfair_Display'] text-[22px] font-medium text-[#2C1810] mb-2">
            {title}
          </h2>
          <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62] leading-relaxed mb-8 max-w-[260px]">
            {message}
          </p>

          <div className="flex gap-3 w-full">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="
                flex-1 h-[52px] px-6
                bg-[#FBEAF0] text-[#D4537E]
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px] border border-[#F4C0D1]
                transition-all duration-150 ease-out
                active:scale-[0.98]
                disabled:opacity-40
                select-none touch-manipulation
              "
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className="
                flex-1 h-[52px] px-6
                bg-white text-[#C0392B]
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px] border border-[#F5C6CB]
                transition-all duration-150 ease-out
                active:bg-[rgba(192,57,43,0.06)]
                active:scale-[0.98]
                disabled:opacity-40
                select-none touch-manipulation
              "
            >
              {loading ? 'Removing…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
